'use client';

import './giving-history.scss';

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Link } from '@sola/ark-core/atoms/link/link.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import { Select } from '@sola/ark-core/molecules/select/select.js';
import { useAuth } from '@sola/auth-sdk/store.tsx';
import { getGivingHistory } from '@sola/proxy-sdk/utils/routes.js';
import { ServiceGivingHistoryItem } from '@sola/schema/dotcom/proxy-api/upstreamPayloads.js';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.js';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks.js';
import { commitmentsQuery } from '@sola/supporter-sdk/queries.js';

import { CTA_CONTACT_SUPPORT } from '../../utils/ctas.ts';
import { GivingHistoryMonth } from './giving-history-month.tsx';
import {
  attachCommitmentToGivingItem,
  formatGivingItemsGroup,
  getYearList,
  groupGivingItemsByMonth,
} from './utils.ts';

type HistoryFetchState = {
  isFetching: boolean;
  hasError: boolean;
  items: ServiceGivingHistoryItem[];
};
const defaultHistoryFetchState = {
  isFetching: false,
  hasError: false,
  items: [],
};

/**
 * Get component data
 */
export const useComponentData = () => {
  const { data, isLoading, isFetching, isPending, isError } =
    useSupporterQuery(commitmentsQuery);
  const { t } = useTranslation('my');

  const { authUser } = useAuth('authUser');
  const { authUserGlobalId } = authUser || {};
  const currentYear = new Date().getFullYear();

  /** NOTE: Could be configured in CMS */
  const numYearsToDisplay = 7;

  // @ts-expect-error //TODO This should be fixed once we tighten up useSupporterQuery types.
  const commitments: GraphQLCommitment[] | undefined = data;

  const [givingHistoryData, setGivingHistoryData] = useState<HistoryFetchState>(
    {
      ...defaultHistoryFetchState,
      isFetching: true,
    },
  );

  /**
   * Fetch raw giving history from proxy-api.
   */
  const getRawGivingHistory = async (supporterId: string, year: number) => {
    setGivingHistoryData({
      ...defaultHistoryFetchState,
      isFetching: true,
    });
    try {
      const { response, statusCode } = await getGivingHistory(supporterId, {
        start: `${year}-01-01`,
        end: `${year}-12-31`,
      });

      if (statusCode !== 200 || !Array.isArray(response)) {
        throw new Error('Failed to fetch giving history');
      }

      setGivingHistoryData({
        ...defaultHistoryFetchState,
        items: response,
      });
    } catch (error) {
      setGivingHistoryData({
        ...defaultHistoryFetchState,
        hasError: true,
      });
    }
  };

  /**
   * Get initial giving history once supporter is ready.
   */
  useEffect(() => {
    if (authUserGlobalId) {
      getRawGivingHistory(authUserGlobalId, currentYear);
    }
  }, [authUserGlobalId]);

  /**
   * Handle year change.
   */
  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    getRawGivingHistory(authUser!.authUserGlobalId, Number(e.target.value));
  };

  /**
   * Group and format items for rendering.
   */
  const groupedItems = useMemo(() => {
    if (!givingHistoryData?.items?.length || !commitments?.length) {
      return null;
    }

    const mappedGivingHistoryItems = givingHistoryData.items.map(
      attachCommitmentToGivingItem(commitments),
    );
    const grouped =
      mappedGivingHistoryItems.length > 0
        ? formatGivingItemsGroup(
            givingHistoryData.items[0].taxYear,
            groupGivingItemsByMonth(mappedGivingHistoryItems),
            t,
          )
            .toReversed()
            .filter((group) => group.givingHistoryItems.length)
        : [];

    return grouped;
    // Checking for length works here because we only rereun when givingHistoryData changes from null to array,
    // and when we have all of commitments from the initial query.
  }, [!!givingHistoryData?.items?.length, !!commitments?.length]);

  const hasQueryResults = !(isLoading || isFetching || isPending) || isError;

  return {
    isShowingSkeletons: givingHistoryData.isFetching || !hasQueryResults,
    yearList: getYearList(new Date(), numYearsToDisplay),
    hasData:
      !!commitments?.length &&
      !!givingHistoryData?.items?.length &&
      groupedItems,
    hasError: givingHistoryData.hasError,
    groupedItems,
    handleYearChange,
  };
};

/**
 * Displays list of commitments based on frequency.
 */
export const GivingHistory = () => {
  const { t } = useTranslation('my');

  const {
    isShowingSkeletons,
    hasData,
    hasError,
    groupedItems,
    yearList,
    handleYearChange,
  } = useComponentData();

  return (
    <div className="max-w-md">
      <div className="flex flex-col gap-snug-lg mb-comfy-xl">
        <div className="GivingHistory__yearselect">
          <Select
            label={t('givingHistory.title')}
            items={yearList.map((y) => {
              return {
                value: y.toString(),
              };
            })}
            onChange={handleYearChange}
            disabled={isShowingSkeletons}
          />
        </div>
      </div>

      {isShowingSkeletons && <GivingHistoryMonth isLoading />}

      {!isShowingSkeletons &&
        hasData &&
        groupedItems!.map((givingMonthGroup) => (
          <GivingHistoryMonth
            key={givingMonthGroup.heading}
            {...givingMonthGroup}
          />
        ))}

      {!isShowingSkeletons && !hasData && !hasError && (
        <Alert message={t('givingHistory.noHistory')} />
      )}

      {!isShowingSkeletons && hasError && (
        <Alert
          title={t('givingHistory.requestError.title')}
          message={t('givingHistory.requestError.message')}
          variant="negative"
          action={
            <Link href={CTA_CONTACT_SUPPORT.url}>
              {t(CTA_CONTACT_SUPPORT.translation)}
            </Link>
          }
        />
      )}
    </div>
  );
};
